import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import {
  AUTH_SESSION_CLEARED_EVENT,
  AUTH_SESSION_UPDATED_EVENT,
} from '~/shared/lib/constants/auth-events'
import { generateCustomEvent } from '~/shared/lib/helper-functions/generate-custom-event'
import { removeAuthStorage } from '~/shared/lib/auth-session'

const AUTH_EXEMPT_ENDPOINTS = ['auth/login']
const TOKEN_RENEWAL_ENDPOINTS = ['auth/refresh']
const AUTH_REFRESH_LOCK_NAME = 'mateline-auth-refresh'

type RefreshTokenResponse = {
  data: {
    token: string
    refreshToken: string
  }
}

type AuthSessionSnapshot = {
  sessionToken: string | null
  refreshToken: string | null
}

type RetryableRequestConfig = AxiosRequestConfig & {
  authRefreshRetried?: boolean
}

declare module 'axios' {
  interface AxiosRequestConfig {
    silentForbidden?: boolean
  }
}

const silentForbiddenErrors = new Map<string, AxiosError>()
let silentForbiddenSessionToken: string | null = null

export const clearSilentForbiddenRequests = () => {
  silentForbiddenErrors.clear()
}

const getSilentForbiddenRequestKey = (config: AxiosRequestConfig) =>
  `${config.method ?? 'get'}:${config.url ?? ''}`

function getAuthSessionSnapshot(): AuthSessionSnapshot {
  return {
    sessionToken: localStorage.getItem('sessionToken'),
    refreshToken: localStorage.getItem('refreshToken'),
  }
}

function hasSessionBeenRefreshed(snapshot: AuthSessionSnapshot): boolean {
  const currentSession = getAuthSessionSnapshot()

  return (
    currentSession.sessionToken !== null &&
    currentSession.refreshToken !== null &&
    (currentSession.sessionToken !== snapshot.sessionToken ||
      currentSession.refreshToken !== snapshot.refreshToken)
  )
}

function hasActiveAuthSession(): boolean {
  const session = getAuthSessionSnapshot()

  return session.sessionToken !== null && session.refreshToken !== null
}

function isTokenRenewalRequest(url?: string): boolean {
  return TOKEN_RENEWAL_ENDPOINTS.some((route) => url?.includes(route))
}

function clearAuthSessionAndRedirect() {
  removeAuthStorage()
  generateCustomEvent(AUTH_SESSION_CLEARED_EVENT)
  window.location.replace('/login')
}

export class ApiBase {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: `${import.meta.env.VITE_API_URL}`,
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
    })

    this.client.interceptors.request.use(async (req) => {
      const isLogin = AUTH_EXEMPT_ENDPOINTS.some((route) =>
        req.url?.includes(route),
      )
      const isRefresh = isTokenRenewalRequest(req.url)

      if (isLogin) return req

      if (isRefresh) {
        const token = localStorage.getItem('refreshToken')

        if (token) {
          req.headers['X-Refresh-Token'] = `${token}`
        }
        return req
      }

      const token = localStorage.getItem('sessionToken')

      if (token !== silentForbiddenSessionToken) {
        clearSilentForbiddenRequests()
        silentForbiddenSessionToken = token
      }

      if (req.silentForbidden) {
        const forbiddenError = silentForbiddenErrors.get(
          getSilentForbiddenRequestKey(req),
        )
        if (forbiddenError) return Promise.reject(forbiddenError)
      }

      localStorage.removeItem('error404')

      if (token) {
        req.headers['X-Auth-Token'] = `${token}`
      }
      return req
    })

    this.client.interceptors.response.use(
      // Here we can update token using response. Need to validate with backend
      (response) => {
        return response
      },
      // Handling 498 error
      async (error) => {
        const originalRequest = error.config as
          | RetryableRequestConfig
          | undefined
        const isRefreshRequest = isTokenRenewalRequest(originalRequest?.url)

        if (isRefreshRequest) {
          return Promise.reject(error)
        }

        if (error instanceof AxiosError && error.response?.status === 404) {
          localStorage.setItem('error404', JSON.stringify(true))
        }

        if (error instanceof AxiosError && error.response?.status === 403) {
          if (originalRequest?.silentForbidden) {
            silentForbiddenErrors.set(
              getSilentForbiddenRequestKey(originalRequest),
              error,
            )
          } else {
            generateCustomEvent('setError', {
              text: 'Доступ запрещён',
              status: 403,
            })
          }
        }

        if (
          error instanceof AxiosError &&
          error.response?.status === 498 &&
          originalRequest &&
          !originalRequest.authRefreshRetried
        ) {
          const sessionSnapshot = getAuthSessionSnapshot()
          originalRequest.authRefreshRetried = true

          try {
            await this.refreshAuthSession(sessionSnapshot)
          } catch (refreshError) {
            if (!hasSessionBeenRefreshed(sessionSnapshot)) {
              clearAuthSessionAndRedirect()
              return Promise.reject(refreshError)
            }
          }

          if (!hasActiveAuthSession()) {
            clearAuthSessionAndRedirect()
            return Promise.reject(error)
          }

          return this.client.request(originalRequest)
        }

        // Handling 401 error
        if (error instanceof AxiosError && error.response?.status === 401) {
          clearAuthSessionAndRedirect()
        }

        return Promise.reject(error)
      },
    )
  }

  private async refreshAuthSession(
    snapshot: AuthSessionSnapshot,
  ): Promise<void> {
    const refreshSession = async () => {
      if (hasSessionBeenRefreshed(snapshot)) {
        return
      }

      const { data } =
        await this.client.post<RefreshTokenResponse>('/auth/refresh')

      localStorage.setItem('sessionToken', data.data.token)
      localStorage.setItem('refreshToken', data.data.refreshToken)
      generateCustomEvent(AUTH_SESSION_UPDATED_EVENT, {
        token: data.data.token,
        refreshToken: data.data.refreshToken,
      })
    }

    if ('locks' in navigator) {
      await navigator.locks.request(
        AUTH_REFRESH_LOCK_NAME,
        { mode: 'exclusive' },
        refreshSession,
      )
      return
    }

    await refreshSession()
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config)
    return response?.data
  }

  public async getBlob(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Blob>> {
    return this.client.get(url, { ...config, responseType: 'blob' })
  }

  public async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }

  public async postBlob(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Blob>> {
    return this.client.post(url, data, { ...config, responseType: 'blob' })
  }

  public async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data)
    return response.data
  }

  public async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<T>(url, data)
    return response.data
  }

  public async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url)
    return response.data
  }

  public async deleteWithBody<T>(url: string, data: unknown): Promise<T> {
    const config: AxiosRequestConfig = {
      data,
    }
    const response: AxiosResponse<T> = await this.client.delete(url, config)
    return response.data
  }
}

export const apiService = new ApiBase()
